import React from 'react';
import pt from 'prop-types';
import { flatten, unflatten } from 'flat';
import { get, set, reduce, isEmpty } from 'lodash';
import Modal from 'components/modal';
import Tabs from 'components/tabs';
import { Form, Field, FieldLocalized } from 'components/form';
import { Section, SectionGroup } from 'components/section';
import { stringifyFileData } from 'components/storefront/editor/utils/theme-config';
import { wordify } from 'shared/utils';

export default class EditThemeLanguageModal extends React.Component {
  static contextTypes = {
    client: pt.object.isRequired,
    openModal: pt.func.isRequired,
    closeModal: pt.func.isRequired,
  };

  static propTypes = {
    swellTheme: pt.object.isRequired,
    onSave: pt.func.isRequired,
  };

  state = {
    edited: false,
    saving: false,
    config: {},
    usingLocaleFiles: false,
    defaultLocale: '',
    tabs: [],
    tabSelected: null,
    fields: {},
    searchValue: '', // Add search state
  };

  constructor(props, context) {
    super(props, context);

    const { config, usingLocaleFiles } = this.getLanguageConfig();

    const tabs = config && this.getSectionTabs(config);
    const defaultLocale = config && this.getDefaultLocale(config);
    const fields = config && this.getSectionFields(config, defaultLocale);

    console.log('EditThemeLanguageModal', { config, fields });

    const tabSelected = tabs?.[0]?.id;

    this.state = {
      ...this.state,
      config,
      usingLocaleFiles,
      values: config,
      defaultLocale,
      tabs,
      tabSelected,
      fields,
    };
  }

  getLanguageConfig() {
    let languageConfig;
    let localeLanguageConfig;

    // Try language.json first
    const languageConfigFile = this.getLanguageConfigFile();
    if (languageConfigFile) {
      languageConfig = languageConfigFile.configData;
    } else {
      // Fall back to locale files
      const localeConfigFiles = this.getLocaleConfigFiles();
      if (localeConfigFiles?.length) {
        localeLanguageConfig = this.getLocaleLanguageConfig(localeConfigFiles);
        if (localeLanguageConfig) {
          languageConfig = localeLanguageConfig;
        }
      }
    }

    return {
      config: languageConfig ? this.normalizeLocales(languageConfig) : null,
      usingLocaleFiles: Boolean(localeLanguageConfig),
    };
  }

  getLanguageConfigFile() {
    const { swellTheme } = this.props;

    const languageConfig = Array.from(swellTheme.themeConfigs).find(
      ([filePath]) => {
        return (
          filePath === 'theme/config/language.json' ||
          // Maintain backward compatibility for a few theme versions
          // TODO: remove this in the near future
          filePath === 'theme/config/translations.json'
        );
      },
    )?.[1];

    if (languageConfig) {
      try {
        const configData = JSON.parse(languageConfig.file_data);
        return {
          ...languageConfig,
          configData,
        };
      } catch (err) {
        console.error(
          'Error parsing language file',
          languageConfig.file_path,
          err,
        );
      }
    }

    return null;
  }

  isLocaleFile(filePath) {
    return (
      filePath.startsWith('theme/locales') &&
      filePath.endsWith('.json') &&
      !filePath.endsWith('schema.json')
    );
  }

  getLocaleConfigFiles() {
    const { swellTheme } = this.props;

    // Themes may have locale files where each language is separate from the default
    return Array.from(swellTheme.themeConfigs)
      .filter(([filePath]) => this.isLocaleFile(filePath))
      .map(([filePath, config]) => {
        try {
          const configData = JSON.parse(config.file_data);
          return {
            ...config,
            configData,
          };
        } catch (err) {
          console.error('Error parsing locale file', filePath, err);
          return null;
        }
      });
  }

  getLocaleLanguageConfig(localeConfigFiles) {
    const { client } = this.context;
    if (!localeConfigFiles?.length) return null;

    // Find the default config (either .default.json or first config)
    const defaultConfig =
      localeConfigFiles.find((config) => {
        return config?.file_path.includes('.default.');
      }) || localeConfigFiles[0];

    if (!defaultConfig?.configData) return null;

    // Create a map of locale codes to their config data
    const localeConfigs = {};
    localeConfigFiles.forEach((config) => {
      if (!config?.file_path || !config.configData) return;

      // Extract locale code from file path (e.g., "theme/locales/en-US.json" -> "en-US")
      const localePath = config.file_path.replace('theme/locales/', '');
      const localeCode = localePath.split('.')[0];
      localeConfigs[localeCode] = config.configData;
    });

    // Get the default locale from either client or locale config
    const fileDefaultLocale = defaultConfig.file_path
      .split('/')
      .pop()
      .split('.')[0];
    const defaultLocale = client?.defaultLocale || fileDefaultLocale;

    // If client's default locale isn't in configs, copy from default
    if (client?.defaultLocale && !localeConfigs[client.defaultLocale]) {
      localeConfigs[client.defaultLocale] = { ...defaultConfig.configData };
    }

    // Initialize result with default locale
    const result = { $default_locale: defaultLocale };

    // Helper to ensure a path exists and get its reference
    const getPath = (obj, path) => {
      let current = obj;
      for (const key of path) {
        if (!current[key]) current[key] = {};
        current = current[key];
      }
      return current;
    };

    // Process each key in the default config to build the structure
    const defaultConfigFlat = flatten(defaultConfig.configData);
    Object.entries(defaultConfigFlat).forEach(([key]) => {
      const parts = key.split('.');

      const category = parts[0];
      const group = parts.length > 2 ? parts[1] : null;
      const field = key.replace(
        group ? `${category}.${group}.` : `${category}.`,
        '',
      );

      // Get/create the group object
      const categoryObj = getPath(result, [category]);
      const groupObj = group ? getPath(categoryObj, [group]) : categoryObj;

      // Initialize $locale if needed
      if (!groupObj.$locale) {
        groupObj.$locale = {};
      }

      // Add translations for each locale
      Object.entries(localeConfigs).forEach(([locale, config]) => {
        if (!groupObj.$locale[locale]) {
          groupObj.$locale[locale] = {};
        }
        set(groupObj.$locale[locale], field, get(config, key));

        if (locale === defaultLocale) {
          set(groupObj, field, get(groupObj.$locale[locale], field));
        }
      });
    });

    return result;
  }

  getDefaultLocale(config = this.state.config) {
    // First check for top-level default locale
    if (config.$default_locale) {
      return config.$default_locale;
    }

    // Helper function to recursively find first locale
    const findFirstLocale = (obj) => {
      if (!obj || typeof obj !== 'object') {
        return null;
      }

      // If we find a $locale object, return its first key
      if (obj.$locale) {
        const locales = Object.keys(obj.$locale);
        if (locales.length > 0) {
          return locales[0];
        }
      }

      // Otherwise recursively search through all object values
      for (const value of Object.values(obj)) {
        if (typeof value === 'object' && !Array.isArray(value)) {
          const locale = findFirstLocale(value);
          if (locale) {
            return locale;
          }
        }
      }

      return null;
    };

    // Find first locale in config
    const firstLocale = findFirstLocale(config);
    if (firstLocale) {
      return firstLocale;
    }

    return null;
  }

  getConfigsUpdated() {
    const { values, usingLocaleFiles } = this.state;

    if (usingLocaleFiles) {
      return this.getLocaleConfigsUpdated();
    }

    // Using language config, return a single file
    const languageConfig = this.getLanguageConfigFile();
    if (!languageConfig) return [];

    return [
      {
        ...languageConfig,
        file_data: stringifyFileData(values),
        configData: undefined, // Unset config data
      },
    ];
  }

  getLocaleConfigsUpdated() {
    const { swellTheme } = this.props;
    const { values } = this.state;

    // Get all existing locale configs for reference
    const existingConfigs = new Map(
      Array.from(swellTheme.themeConfigs).filter(([filePath]) =>
        this.isLocaleFile(filePath),
      ),
    );

    // Get all locales from the values object
    const allLocales = new Set();
    const findLocales = (obj) => {
      if (!obj || typeof obj !== 'object') return;
      if (obj.$locale) {
        Object.keys(obj.$locale).forEach((locale) => allLocales.add(locale));
      }
      Object.values(obj).forEach(findLocales);
    };
    findLocales(values);

    // Process each locale (both existing and new)
    return Array.from(allLocales).map((localeCode) => {
      const localeValues = {};

      // Helper to process a group's translations
      const processGroup = (groupObj, prefix = '') => {
        if (!groupObj || prefix === '$locale' || prefix === '$default_locale') {
          return;
        }

        // If this group has a $locale object, extract translations for this locale
        if (groupObj.$locale?.[localeCode]) {
          Object.entries(groupObj.$locale[localeCode]).forEach(
            ([key, value]) => {
              const fullKey = prefix ? `${prefix}.${key}` : key;
              localeValues[fullKey] = value;
            },
          );
        }

        // Process nested groups
        Object.entries(groupObj).forEach(([key, value]) => {
          if (key !== '$locale' && typeof value === 'object') {
            const newPrefix = prefix ? `${prefix}.${key}` : key;
            processGroup(value, newPrefix);
          }
        });
      };

      // Start processing from the root level categories
      Object.entries(values).forEach(([category, categoryObj]) => {
        if (category !== '$default_locale') {
          processGroup(categoryObj, category);
        }
      });

      // Determine if this is an existing config or needs to be created
      const getExistingConfig = () => {
        const defaultConfigPath = `theme/locales/${localeCode}.json`;
        const configPaths = [
          defaultConfigPath,
          `theme/locales/${localeCode}.default.json`,
        ];

        if (localeCode.includes('-')) {
          const baseLocaleCode = localeCode.split('-')[0];

          configPaths.push(
            `theme/locales/${baseLocaleCode}.json`,
            `theme/locales/${baseLocaleCode}.default.json`,
          );
        }

        for (const configPath of configPaths) {
          const existingConfig = existingConfigs.get(configPath);

          if (existingConfig) {
            return { existingConfig, filePath: configPath };
          }
        }

        return { filePath: defaultConfigPath, name: localeCode };
      };

      const { existingConfig, filePath } = getExistingConfig();
      const localeData = unflatten(localeValues, { object: true });

      if (existingConfig) {
        // Update existing config
        return {
          ...existingConfig,
          file_data: stringifyFileData(localeData),
        };
      } else {
        // Create new config
        return {
          file_path: filePath,
          file_data: stringifyFileData(localeData),
        };
      }
    });
  }

  onSubmitForm = async () => {
    const { onSave } = this.props;

    this.setState({ saving: true });

    const configs = this.getConfigsUpdated();
    const success = await onSave(configs);

    this.setState({ saving: false });

    if (success) {
      this.context.closeModal();
    }
  };

  onChangeForm = (values, edited) => {
    this.setState({
      values: { ...values, search: undefined },
      edited,
    });
  };

  onCloseModal = (event) => {
    if (this.state.edited) {
      event.preventDefault();
      this.context.openModal('ConfirmRouteLeave', {
        onConfirm: () => {
          this.context.closeModal();
        },
      });
    } else {
      this.context.closeModal();
    }
  };

  getSearchTab(searchValue, prevSearchValue) {
    const isSearchStart = searchValue && !prevSearchValue;
    const isSearchEnd = !searchValue && prevSearchValue;

    return isSearchStart
      ? '_search_results'
      : isSearchEnd
      ? this.state.tabs[0]?.id
      : this.state.tabSelected;
  }

  onChangeSearch = (_event, searchValue) => {
    const { fields } = this.state;

    const filteredFields = this.filterFields(fields, searchValue);

    this.setState((prevState) => {
      const tabSelected = this.getSearchTab(searchValue, prevState.searchValue);
      return {
        ...prevState,
        filteredFields,
        searchValue,
        tabSelected,
      };
    });
  };

  onClickTab = (tabSelected) => {
    const { filteredFields } = this.state;

    this.setState({
      tabSelected,
      // Clear filters when tab changes
      ...(filteredFields && tabSelected !== '_search_results'
        ? {
            filteredFields: null,
            searchValue: '',
          }
        : {}),
    });
  };

  filterFields(fields, searchValue) {
    if (!searchValue) return null;

    const searchRegex = new RegExp(searchValue, 'i');
    const allMatchingFields = [];

    Object.entries(fields).forEach(([tabKey, tabData]) => {
      if (!tabData?.fields) return;

      const matchingFields = tabData.fields
        .map((group) => {
          if (group.fields) {
            // Filter fields within a group
            const filteredGroupFields = group.fields.filter(
              (field) =>
                searchRegex.test(field.label) ||
                searchRegex.test(field.defaultValue?.toString() || '') ||
                (field.localeValue &&
                  Object.values(field.localeValue).some((localeObj) => {
                    return searchRegex.test(
                      localeObj[field.key]?.toString() || '',
                    );
                  })),
            );

            return filteredGroupFields.length
              ? {
                  ...group,
                  fields: filteredGroupFields,
                  category: tabKey, // Add category info
                }
              : null;
          } else {
            // Filter individual fields
            return searchRegex.test(group.label) ||
              searchRegex.test(group.defaultValue?.toString() || '') ||
              (group.localeValue &&
                Object.values(group.localeValue).some((localeObj) =>
                  searchRegex.test(localeObj[group.key]?.toString() || ''),
                ))
              ? { ...group, category: tabKey }
              : null; // Add category info
          }
        })
        .filter(Boolean);

      allMatchingFields.push(...matchingFields);
    });

    const filteredFields = allMatchingFields.reduce((acc, group) => {
      acc.push(...group.fields.map(({ name }) => name));
      return acc;
    }, []);

    return filteredFields;
  }

  getSectionTabs(config = this.state.config) {
    return reduce(
      config,
      (acc, _value, key) => {
        acc.push({
          id: key,
          label: wordify(key),
        });
        return acc;
      },
      [],
    )
      .filter((tab) => !isEmpty(config[tab.id]) && tab.id[0] !== '$')
      .sort((a, b) => {
        // put the `general` tab first if it exists
        if (a.id === 'general') {
          return -1;
        }
        if (b.id === 'general') {
          return 1;
        }
        // sort alphabetically
        return a.label.localeCompare(b.label);
      });
  }

  getSectionFields(
    config = this.state.config,
    defaultLocale = this.state.defaultLocale,
  ) {
    return Object.entries(config).reduce(
      (categories, [categoryKey, categoryValue]) => {
        // Skip if not an object
        if (typeof categoryValue !== 'object' || Array.isArray(categoryValue)) {
          return categories;
        }

        const category = {
          key: categoryKey,
          label: wordify(categoryKey),
          fields: [],
          localeValue: categoryValue.$locale,
        };

        // Create a default group for ungrouped fields
        const defaultGroup = {
          key: 'default',
          label: undefined,
          fields: [],
          localeValue: null,
        };

        // Process fields in this category/group
        for (const [key, value] of Object.entries(categoryValue)) {
          // Skip $locale as we handle it separately
          if (key === '$locale') continue;

          if (typeof value === 'object' && !Array.isArray(value)) {
            // This is a group
            const group = {
              key,
              label: wordify(key),
              fields: [],
              localeValue: value.$locale,
            };

            // Process fields in the group
            const groupValueFlat = flatten({ ...value, $locale: undefined });
            for (const [fieldKey, fieldValue] of Object.entries(
              groupValueFlat,
            )) {
              if (fieldKey === '$locale') continue;

              // This is a simple field
              group.fields.push({
                key: fieldKey,
                label: wordify(fieldKey),
                name: `${categoryKey}.${key}.${fieldKey}`,
                defaultValue: fieldValue,
                ...(defaultLocale
                  ? {
                      localeFieldPrefix: `${categoryKey}.${key}`,
                      localeValue: value.$locale,
                    }
                  : undefined),
              });
            }

            // If group has $locale, extract fields from it
            if (value.$locale) {
              // Get all unique field keys across all locales
              const localeFieldKeys = new Set();
              Object.values(value.$locale).forEach((localeObj) => {
                Object.keys(flatten(localeObj)).forEach((key) =>
                  localeFieldKeys.add(key),
                );
              });

              // Add each locale field
              for (const fieldKey of localeFieldKeys) {
                if (!group.fields.find((f) => f.key === fieldKey)) {
                  group.fields.push({
                    key: fieldKey,
                    label: wordify(fieldKey),
                    name: `${categoryKey}.${key}.${fieldKey}`,
                    defaultValue:
                      value.$locale[defaultLocale]?.[fieldKey] || '',
                    ...(defaultLocale
                      ? {
                          localeFieldPrefix: `${categoryKey}.${key}`,
                          localeValue: value.$locale,
                        }
                      : undefined),
                  });
                }
              }
            }

            if (group.fields.length > 0) {
              category.fields.push(group);
            }
          } else {
            // This is a direct field in the category - add to default group
            defaultGroup.fields.push({
              key,
              label: wordify(key),
              name: `${categoryKey}.${key}`,
              defaultValue: value,
              ...(defaultLocale
                ? {
                    localeFieldPrefix: categoryKey,
                    localeValue: categoryValue.$locale,
                  }
                : undefined),
            });
          }
        }

        // If category has $locale, extract fields and add to default group
        if (categoryValue.$locale) {
          // Get all unique field keys across all locales
          const localeFieldKeys = new Set();
          Object.values(categoryValue.$locale).forEach((localeObj) => {
            Object.keys(localeObj).forEach((key) => localeFieldKeys.add(key));
          });

          // Add each locale field to default group
          for (const fieldKey of localeFieldKeys) {
            if (!defaultGroup.fields.find((f) => f.key === fieldKey)) {
              defaultGroup.fields.push({
                key: fieldKey,
                label: wordify(fieldKey),
                name: `${categoryKey}.${fieldKey}`,
                defaultValue:
                  categoryValue.$locale[defaultLocale]?.[fieldKey] || '',
                ...(defaultLocale
                  ? {
                      localeFieldPrefix: categoryKey,
                      localeValue: categoryValue.$locale,
                    }
                  : undefined),
              });
            }
          }
          defaultGroup.localeValue = categoryValue.$locale;
        }

        // Add default group to category if it has fields
        if (defaultGroup.fields.length > 0) {
          category.fields.unshift(defaultGroup); // Add default group at the beginning
        }

        categories[categoryKey] = category;
        return categories;
      },
      {},
    );
  }

  normalizeLocales(config) {
    const { client } = this.context;
    if (!client?.locales?.length) return config;

    // Helper to get base language code (e.g., "en" from "en-US")
    const getBaseCode = (code) => code.split('-')[0];

    // Map client locales for easier lookup
    const clientLocales = client.locales.reduce((acc, locale) => {
      const baseCode = getBaseCode(locale.code);
      if (!acc[baseCode]) {
        acc[baseCode] = [];
      }
      acc[baseCode].push(locale.code);
      return acc;
    }, {});

    // Deep clone config to avoid mutations
    const newConfig = JSON.parse(JSON.stringify(config));

    // Recursively process all $locale objects
    const processObject = (obj) => {
      if (!obj || typeof obj !== 'object') return;

      if (obj.$locale) {
        const existingCodes = Object.keys(obj.$locale);
        if (!existingCodes.length) return;

        // Group existing codes by base language
        const existingByBase = existingCodes.reduce((acc, code) => {
          const baseCode = getBaseCode(code);
          if (!acc[baseCode]) {
            acc[baseCode] = [];
          }
          acc[baseCode].push(code);
          return acc;
        }, {});

        // For each base language in the config
        Object.entries(existingByBase).forEach(
          ([baseCode, existingLangCodes]) => {
            const clientCodesForBase = clientLocales[baseCode] || [];

            if (
              existingLangCodes.length === 1 &&
              clientCodesForBase.length === 1
            ) {
              // Simple 1:1 mapping - can safely rename
              const [existingCode] = existingLangCodes;
              const [clientCode] = clientCodesForBase;

              if (existingCode !== clientCode) {
                obj.$locale[clientCode] = obj.$locale[existingCode];
                delete obj.$locale[existingCode];
              }
            } else if (
              existingLangCodes.length === 1 &&
              clientCodesForBase.length > 0
            ) {
              // Config has one code (e.g., "en") but client has specific variants (e.g., "en-US")
              // Copy to the first client locale but keep original if it's a long code
              const [existingCode] = existingLangCodes;
              const [clientCode] = clientCodesForBase;

              if (existingCode.includes('-')) {
                // Keep existing long code and copy to client code
                obj.$locale[clientCode] = { ...obj.$locale[existingCode] };
              } else {
                // Replace short code with client code
                obj.$locale[clientCode] = obj.$locale[existingCode];
                delete obj.$locale[existingCode];
              }
            } else if (
              existingLangCodes.length > 1 &&
              clientCodesForBase.length === 1
            ) {
              // Config has multiple variants but client has only base language
              // Copy first existing code to client code but keep all existing codes
              const [clientCode] = clientCodesForBase;
              if (!obj.$locale[clientCode]) {
                obj.$locale[clientCode] = {
                  ...obj.$locale[existingLangCodes[0]],
                };
              }
            }
          },
        );

        // Add any missing client locales, copying from matching base language
        client.locales.forEach(({ code: clientCode }) => {
          if (!obj.$locale[clientCode]) {
            const baseCode = getBaseCode(clientCode);
            const existingCode = Object.keys(obj.$locale).find(
              (code) => getBaseCode(code) === baseCode,
            );
            if (existingCode) {
              obj.$locale[clientCode] = { ...obj.$locale[existingCode] };
            }
          }
        });
      }

      // Recursively process all nested objects
      Object.values(obj).forEach(processObject);
    };

    processObject(newConfig);
    return newConfig;
  }

  isFieldVisible(field, filteredFields, tabKey) {
    const { tabSelected } = this.state;
    const isFiltered = filteredFields && filteredFields.includes(field.name);
    const isSearchTab = isFiltered && tabSelected === '_search_results';

    return (isFiltered && isSearchTab) || tabSelected === tabKey;
  }

  anyFieldsVisible(fields, filteredFields, tabKey) {
    return fields.some((field) =>
      this.isFieldVisible(field, filteredFields, tabKey),
    );
  }

  renderFields(fields, localeValue, tabKey) {
    const { filteredFields } = this.state;

    return fields.map((field) => (
      <FieldLocalized
        type="text"
        key={field.name}
        name={field.name}
        label={field.label}
        className={
          this.isFieldVisible(field, filteredFields, tabKey)
            ? undefined
            : 'hidden'
        }
        defaultValue={field.defaultValue}
        localeFieldPrefix={field.localeFieldPrefix}
        localeFieldName={field.key}
        localeValue={localeValue || {}}
        localized={Boolean(field.localeFieldPrefix)}
      />
    ));
  }

  render() {
    const {
      config,
      tabs,
      tabSelected,
      fields,
      filteredFields,
      searchValue,
      edited,
      saving,
    } = this.state;

    return (
      <Form
        onSubmit={this.onSubmitForm}
        onChange={this.onChangeForm}
        values={config}
      >
        <Modal
          title="Theme language content"
          width={950}
          onClose={this.onCloseModal}
          actions={[
            { label: 'Save', type: 'submit', disabled: !edited || saving },
          ]}
          localized={Boolean(config)}
          hasTabs={true}
          tapout={false}
          loading={saving}
          headerControls={
            <>
              <Field
                name="search"
                type="text"
                placeholder="Search items"
                defaultValue={searchValue}
                onChange={this.onChangeSearch}
              />
              <Tabs
                name="tab"
                items={[
                  ...(filteredFields
                    ? [{ id: '_search_results', label: 'Search results' }]
                    : []),
                  ...tabs,
                ]}
                active={tabSelected}
                onClick={this.onClickTab}
                overflow={true}
                overflowProps={
                  {
                    //side: 'top',
                  }
                }
              />
            </>
          }
        >
          {config ? (
            <SectionGroup>
              {Object.keys(fields).map((tabKey) =>
                fields[tabKey]?.fields ? (
                  fields[tabKey].fields.map((field) => (
                    <>
                      {field.fields?.length > 0 ? (
                        <Section
                          key={`${tabKey}.${field.key}`}
                          title={[
                            filteredFields ? fields[tabKey].label : undefined,
                            field.label,
                          ]
                            .filter(Boolean)
                            .join(' > ')}
                          className={
                            this.anyFieldsVisible(
                              field.fields,
                              filteredFields,
                              tabKey,
                            )
                              ? undefined
                              : 'hidden'
                          }
                        >
                          {this.renderFields(
                            field.fields,
                            field.localeValue,
                            tabKey,
                          )}
                        </Section>
                      ) : (
                        <Section
                          key={`${tabKey}.default`}
                          className={
                            this.anyFieldsVisible(
                              field.fields,
                              filteredFields,
                              tabKey,
                            )
                              ? undefined
                              : 'hidden'
                          }
                        >
                          {this.renderFields(
                            [field],
                            field.localeValue,
                            tabKey,
                          )}
                        </Section>
                      )}
                    </>
                  ))
                ) : (
                  <p>There are no items in this category.</p>
                ),
              )}
              {filteredFields?.length === 0 && (
                <p>No items found matching '{searchValue}'</p>
              )}
            </SectionGroup>
          ) : (
            <p>Language configiration not found or not formatted correctly.</p>
          )}
        </Modal>
      </Form>
    );
  }
}
